import { useState, useEffect, useCallback } from 'react';
import api from '../services/api.ts';
import { getWahaSessionStatus } from '../services/api.ts';
import { getOfficialWhatsAppConfig } from '../services/api.ts';

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'qr' | null;

// Estados para detecção inteligente de restarts
interface RestartDetectionState {
  consecutiveErrors: number;
  lastSuccessfulStatus: ConnectionStatus | null;
  lastErrorTime: number | null;
  inMaintenanceMode: boolean;
  maintenanceStartTime: number | null;
  lastValidStatus: WhatsAppStatusData | null;
}

export interface WhatsAppStatusData {
  connected: boolean;
  state: string;
  provider?: 'zapi' | 'waha' | 'official_whatsapp' | null;
  sessionName?: string;
  status?: string; // Para WAHA: STOPPED, STARTING, SCAN_QR_CODE, WORKING, FAILED
}

export interface UseWhatsAppStatusReturn {
  whatsappStatus: WhatsAppStatusData | null;
  connectionStatus: ConnectionStatus;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isMaintenanceMode: boolean; // 🆕 Modo manutenção detectado
}

export const useWhatsAppStatus = (): UseWhatsAppStatusReturn => {
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatusData | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Estado para detecção inteligente de restarts
  const [restartDetection, setRestartDetection] = useState<RestartDetectionState>({
    consecutiveErrors: 0,
    lastSuccessfulStatus: null,
    lastErrorTime: null,
    inMaintenanceMode: false,
    maintenanceStartTime: null,
    lastValidStatus: null
  });

  const getWhatsAppConnectionStatus = useCallback((provider: string, status: any): ConnectionStatus => {
    if (!provider) return 'disconnected';

    if (provider === 'waha') {
      switch (status?.status) {
        case 'WORKING':
          return 'connected';
        case 'SCAN_QR_CODE':
          return 'qr';
        case 'STARTING':
          return 'connecting';
        case 'STOPPED':
        case 'FAILED':
          return 'disconnected';
        default:
          return 'disconnected';
      }
    }

    if (provider === 'zapi') {
      return status?.connected ? 'connected' : 'disconnected';
    }

    if (provider === 'official_whatsapp') {
      return 'connected';
    }

    return 'disconnected';
  }, []);

  // Função para detectar se é erro de servidor temporário vs desconexão real
  const detectServerError = useCallback((err: any): 'server_error' | 'real_disconnect' => {
    console.log('🔍 [detectServerError] Analisando erro:', err.response?.status, err.code);

    // Erros que indicam servidor em restart/manutenção
    if (err.code === 'NETWORK_ERROR' ||
      err.code === 'ECONNREFUSED' ||
      err.code === 'ETIMEDOUT' ||
      err.message?.includes('Network Error') ||
      err.message?.includes('Failed to fetch')) {
      console.log('🔧 [detectServerError] Detectado erro de rede/servidor');
      return 'server_error';
    }

    // Status HTTP que indicam servidor em manutenção
    const httpStatus = err.response?.status;
    if (httpStatus === 503 ||  // Service Unavailable
      httpStatus === 502 ||  // Bad Gateway
      httpStatus === 504 ||  // Gateway Timeout
      httpStatus === 429 ||  // Too Many Requests (sobrecarga)
      httpStatus === 500) {  // Internal Server Error
      console.log('🔧 [detectServerError] Detectado erro HTTP de servidor:', httpStatus);
      return 'server_error';
    }

    // Erros que indicam desconexão real do WhatsApp
    if (httpStatus === 401 ||  // Unauthorized - sessão expirada
      httpStatus === 404) {  // Not Found - sessão não existe
      console.log('📱 [detectServerError] Detectado erro de WhatsApp real:', httpStatus);
      return 'real_disconnect';
    }

    // Erros de rede genéricos - tratamos como erro de servidor por segurança
    if (err.message?.includes('fetch') ||
      err.message?.includes('network') ||
      err.message?.includes('connection')) {
      console.log('🔧 [detectServerError] Erro de rede genérico, tratando como server_error');
      return 'server_error';
    }

    // Por padrão, consideramos como erro de servidor para não mostrar popup desnecessário
    console.log('⚠️ [detectServerError] Erro não categorizado, tratando como server_error');
    return 'server_error';
  }, []);

  // Função para atualizar estado de detecção de restart
  const updateRestartDetection = useCallback((
    hasError: boolean,
    errorType: 'server_error' | 'real_disconnect' | null,
    newStatus?: WhatsAppStatusData
  ) => {
    const now = Date.now();

    setRestartDetection(prev => {
      let newState = { ...prev };

      if (hasError && errorType === 'server_error') {
        // Incrementar erros consecutivos
        newState.consecutiveErrors = (prev.consecutiveErrors || 0) + 1;
        newState.lastErrorTime = now;

        console.log(`🔢 [updateRestartDetection] Erros consecutivos: ${newState.consecutiveErrors}`);

        // Se tiver 2+ erros consecutivos em menos de 2 minutos, entrar em modo manutenção
        if (newState.consecutiveErrors >= 2 &&
          prev.lastErrorTime &&
          (now - prev.lastErrorTime) < 120000) { // 2 minutos
          console.log('🛠️ [updateRestartDetection] MODO MANUTENÇÃO ATIVADO');
          newState.inMaintenanceMode = true;
          newState.maintenanceStartTime = now;
        }

      } else if (hasError && errorType === 'real_disconnect') {
        // Resetar detecção de restart - é desconexão real
        console.log('📱 [updateRestartDetection] Desconexão real detectada, resetando manutenção');
        newState.consecutiveErrors = 0;
        newState.inMaintenanceMode = false;
        newState.maintenanceStartTime = null;

      } else if (!hasError && newStatus) {
        // Sucesso - resetar contadores e salvar último status válido
        console.log('✅ [updateRestartDetection] Status obtido com sucesso, resetando contadores');
        newState.consecutiveErrors = 0;
        newState.inMaintenanceMode = false;
        newState.maintenanceStartTime = null;
        newState.lastValidStatus = newStatus;
        newState.lastSuccessfulStatus = getWhatsAppConnectionStatus(newStatus.provider || '', newStatus);
      }

      // Se estiver em modo manutenção há mais de 10 minutos, sair do modo
      if (newState.inMaintenanceMode &&
        newState.maintenanceStartTime &&
        (now - newState.maintenanceStartTime) > 600000) { // 10 minutos
        console.log('⏰ [updateRestartDetection] Saindo do modo manutenção (timeout)');
        newState.inMaintenanceMode = false;
        newState.maintenanceStartTime = null;
        newState.consecutiveErrors = 0;
      }

      return newState;
    });
  }, [getWhatsAppConnectionStatus]);

  const fetchStatus = useCallback(async () => {
    try {
      setError(null);

      // A conexão OAuth não usa os campos legados consultados por /whatsapp/config.
      // Um canal oficial conectado mantém a clínica online mesmo sem sessão WAHA.
      let officialStatus: ConnectionStatus = null;
      try {
        const official = await getOfficialWhatsAppConfig();
        if (official.has_official && ['connected', 'legacy_connected'].includes(official.status || '')) {
          const formattedStatus: WhatsAppStatusData = {
            connected: true, state: 'connected', provider: 'official_whatsapp',
            sessionName: 'WhatsApp Business API',
          };
          setWhatsappStatus(formattedStatus);
          setConnectionStatus('connected');
          updateRestartDetection(false, null, formattedStatus);
          return;
        }
        if (official.status === 'provisioning') officialStatus = 'connecting';
      } catch {
        // Não inferir conexão a partir de credenciais antigas ou de falha da API.
        // Outros canais continuam podendo comprovar seu próprio estado.
      }

      // 1. Buscar configuração dos demais canais
      const configResponse = await api.get('/webhook/whatsapp/config');
      const { provider, config } = configResponse.data;

      console.log('📡 [useWhatsAppStatus] WhatsApp provider:', provider);

      if (!provider || !config) {
        console.log('❌ [useWhatsAppStatus] Nenhum provider configurado');
        const statusData: WhatsAppStatusData = { connected: false, state: officialStatus || 'no_config', provider: officialStatus ? 'official_whatsapp' : null };
        setWhatsappStatus(statusData);
        setConnectionStatus(officialStatus || 'disconnected');
        return;
      }

      let statusData: any = null;

      // 2. Buscar status específico do provider
      if (provider === 'waha') {
        try {
          statusData = await getWahaSessionStatus();
          console.log('📊 [useWhatsAppStatus] Status WAHA:', statusData.status, '-', statusData.connected ? 'connected' : 'disconnected');

          const formattedStatus: WhatsAppStatusData = {
            connected: statusData.connected,
            state: statusData.status,
            provider: 'waha',
            sessionName: statusData.name,
            status: statusData.status
          };

          setWhatsappStatus(formattedStatus);
          const computedStatus = getWhatsAppConnectionStatus('waha', statusData);
          setConnectionStatus(computedStatus === 'disconnected' ? officialStatus || computedStatus : computedStatus);

          // Atualizar detecção de restart com sucesso
          updateRestartDetection(false, null, formattedStatus);

        } catch (wahaError: any) {
          console.error('❌ [useWhatsAppStatus] Erro ao buscar status WAHA:', wahaError);

          const errorType = detectServerError(wahaError);

          // Atualizar detecção de restart com erro
          updateRestartDetection(true, errorType);

          // Se estiver em modo manutenção, usar último status válido se existir
          if (restartDetection.inMaintenanceMode && restartDetection.lastValidStatus) {
            console.log('🛠️ [useWhatsAppStatus] Modo manutenção: usando último status válido');
            setWhatsappStatus(restartDetection.lastValidStatus);
            setConnectionStatus(restartDetection.lastSuccessfulStatus || 'disconnected');
            return;
          }

          // Se for erro de servidor (manutenção), não mudar status para desconectado ainda
          if (errorType === 'server_error') {
            console.log('🔧 [useWhatsAppStatus] Erro de servidor detectado, mantendo status atual');
            // Manter status atual sem mostrar desconectado
            return;
          }

          // Se WAHA falhar com erro real, tenta determinar status baseado no erro
          if (wahaError.response?.status === 404) {
            // Sessão não existe - desconectado real
            const statusData: WhatsAppStatusData = { connected: false, state: 'session_not_found', provider: 'waha' };
            setWhatsappStatus(statusData);
            setConnectionStatus(officialStatus || 'disconnected');
          } else {
            const statusData: WhatsAppStatusData = { connected: false, state: 'error', provider: 'waha' };
            setWhatsappStatus(statusData);
            setConnectionStatus(officialStatus || 'disconnected');
          }
        }

      } else if (provider === 'zapi') {
        const statusResponse = await api.get('/webhook/whatsapp/status');
        statusData = statusResponse.data;
        console.log('📊 [useWhatsAppStatus] Status Z-API:', statusData.connected ? 'connected' : 'disconnected');

        const formattedStatus: WhatsAppStatusData = {
          connected: statusData.connected,
          state: statusData.connected ? 'connected' : 'disconnected',
          provider: 'zapi',
          sessionName: config.instance_id
        };

        const computedStatus = getWhatsAppConnectionStatus('zapi', statusData);
        setWhatsappStatus(formattedStatus);
        setConnectionStatus(computedStatus === 'disconnected' ? officialStatus || computedStatus : computedStatus);

        // Atualizar detecção de restart com sucesso
        updateRestartDetection(false, null, formattedStatus);

      } else if (provider === 'official_whatsapp') {
        // A presença de um token legado não prova conexão ativa.
        const formattedStatus: WhatsAppStatusData = {
          connected: false,
          state: officialStatus || 'disconnected',
          provider: 'official_whatsapp', // Ajustado a tipagem se necessário
          sessionName: 'WhatsApp Business API'
        };

        setWhatsappStatus(formattedStatus);
        setConnectionStatus(officialStatus || 'disconnected');
      }

    } catch (err: any) {
      console.error('❌ [useWhatsAppStatus] Erro geral ao buscar status:', err);

      const errorType = detectServerError(err);

      // Atualizar detecção de restart com erro
      updateRestartDetection(true, errorType);

      // Se estiver em modo manutenção, usar último status válido se existir
      if (restartDetection.inMaintenanceMode && restartDetection.lastValidStatus) {
        console.log('🛠️ [useWhatsAppStatus] Modo manutenção: usando último status válido');
        setWhatsappStatus(restartDetection.lastValidStatus);
        setConnectionStatus(restartDetection.lastSuccessfulStatus || 'disconnected');
        setError('Servidor em manutenção...');
        return;
      }

      // Se for erro de servidor (manutenção), não mudar status para desconectado ainda
      if (errorType === 'server_error') {
        console.log('🔧 [useWhatsAppStatus] Erro de servidor detectado, mantendo status atual');
        setError('Servidor temporariamente indisponível...');
        // Manter status atual sem mostrar desconectado
        return;
      }

      let errorMessage = 'Não foi possível verificar o status do WhatsApp';
      if (err.response?.status === 401) {
        errorMessage = 'Sessão expirada';
      } else if (err.response?.status === 404) {
        errorMessage = 'Sessão não encontrada';
      } else if (err.code === 'NETWORK_ERROR') {
        errorMessage = 'Erro de conexão';
      }

      setError(errorMessage);

      // Status fallback (apenas para erros reais de WhatsApp)
      setWhatsappStatus({
        connected: false,
        state: 'error',
        provider: null
      });
      setConnectionStatus('disconnected');

    } finally {
      setLoading(false);
    }
  }, [getWhatsAppConnectionStatus]);

  // Buscar status inicial
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Configurar polling para verificar status a cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      // Apenas fazer polling se não estiver carregando
      if (!loading) {
        fetchStatus();
      }
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchStatus, loading]);

  // Adicionar listener para eventos de visibilidade da página
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !loading) {
        fetchStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchStatus, loading]);

  return {
    whatsappStatus,
    connectionStatus,
    loading,
    error,
    refetch: fetchStatus,
    isMaintenanceMode: restartDetection.inMaintenanceMode
  };
};
